import history from '../history';

import { Filter } from '../types/filters';
import { Sort, SortDirection, SortColumn, SORT_EMPTY } from '../types/sort';

export function serializeListViewState(
  filterList: Array<Filter>,
  searchText: string = '',
  sort: Sort = SORT_EMPTY,
  extraParams: { [key: string]: any } = {},
): string {
  let params: any = Object.fromEntries(
    filterList.map((filter) => {
      const value = JSON.stringify(filter.value);
      return [
        `${filter.definitionId},${filter.operatorId},${filter.id}`,
        value,
      ];
    }),
  );

  if (searchText.length > 0) {
    params.search = searchText;
  }

  if (sort.column && sort.direction !== 'none') {
    params.sort = `${sort.column},${sort.direction}`;
  }

  return new URLSearchParams({ ...params, ...extraParams }).toString();
}

// Filtering parameters used to be stored differently in the url.
//
// To gracefully migrate to the new version, the deserialization code uses this struture to convert
// each legacy parameter into an array of filters that it should be converted to in the new
// filtering serialization method.
const LEGACY_FILTER_DEFINITIONS: {
  [name: string]: (key: string, value: string) => Array<Filter>;
} = {
  accuracyrange: (key, value) => {
    const data = JSON.parse(value);

    const lowerBound = parseInt(data.range[0], 10);
    const upperBound = parseInt(data.range[1], 10);

    return [
      Filter.create('total_accuracy', 'gt', lowerBound),
      Filter.create('total_accuracy', 'lt', upperBound),
    ];
  },
  spacefunction: (key, value) => {
    const data = JSON.parse(value);
    return [Filter.create('space_function', 'in', data.ids)];
  },
  inmpi: (key, value) => {
    const data = JSON.parse(value);
    return [Filter.create('in_mpi', 'equals', data.inMPI)];
  },
  fullyannotated: (key, value) => {
    const data = value === 'true';
    return [Filter.create('fully_annotated', 'equals', data)];
  },
};

export function deserializeListViewState(
  queryString: string,
  extraKeys = [],
  updateLegacyParamsInPlace = true /* if true, overwrite legacy params in the url */,
): {
  filters: Array<Filter>;
  searchText: string;
  sort: Sort;
  extra: { [key: string]: string }; // Extra parameters that were not accounted for
} {
  const params = new URLSearchParams(queryString);
  let search = params.get('search'),
    sort = params.get('sort');

  let sortDirection: SortDirection = 'none',
    sortColumn: SortColumn = null;
  if (sort) {
    [sortColumn, sortDirection] = sort.split(',') as any;
  }
  if (!search) {
    search = '';
  }

  const filterParams = Array.from(params.entries()).filter(
    ([k, v]) => k !== 'search' && k !== 'sort',
  ) as [string, any];

  let shouldUpdateLegacyParams = false;

  const filters = filterParams
    .filter(([key, value]) => !extraKeys.includes(key))
    .flatMap(([key, value]) => {
      // If this parameters is a legacy parameter, convert to the new filtering mechanism.
      const legacyKeyPrefix = key.split('_')[0];
      if (LEGACY_FILTER_DEFINITIONS[legacyKeyPrefix]) {
        const replacementFilters = LEGACY_FILTER_DEFINITIONS[legacyKeyPrefix](
          key,
          value,
        );

        // Update the query parameters with the serialized result of the new filters
        params.delete(key);
        const replacementParams = new URLSearchParams(
          serializeListViewState(replacementFilters),
        );
        Array.from(replacementParams.entries()).forEach(([k, v]) => {
          params.set(k, v);
        });

        shouldUpdateLegacyParams = true;
        return replacementFilters;
      }

      // A regular non-legacy parameter
      const parsedValue = JSON.parse(value);
      const [definitionId, operatorId, id] = key.split(',');
      return [{ definitionId, operatorId, id, value: parsedValue }];
    });

  const result = {
    filters,
    searchText: search,
    sort: {
      column: sortColumn,
      direction: sortDirection,
    },
    extra: Object.fromEntries(
      filterParams.filter(([key, value]) => extraKeys.includes(key)),
    ),
  };

  if (updateLegacyParamsInPlace && shouldUpdateLegacyParams) {
    const serialized = serializeListViewState(
      result.filters,
      result.searchText,
      result.sort,
      result.extra,
    );
    updateQueryString(serialized, { replace: true });
  }

  return result;
}

// Update the query string in the url
//
// Optionally takes an option of whether to push the new querystring update to
// the history stack, or to replace the querystring in the current history item
// (and not making a new entry on the history stack)
export function updateQueryString(
  querystring: string,
  opts = { replace: true },
) {
  if (!querystring.startsWith('?')) {
    querystring = `?${querystring}`;
  }
  if (querystring === history.location.search) {
    return;
  }

  if (opts.replace) {
    history.replace(querystring);
  } else {
    history.push(querystring);
  }
}

export function updateQueryStringKey(
  key: string,
  value: string | number,
  opts = { replace: true },
) {
  const querystring = new URLSearchParams(
    history.location.search.indexOf('?') >= 0
      ? history.location.search.replace(/^.*\?/, '')
      : '',
  );
  querystring.set(key, `${value}`);
  updateQueryString(querystring.toString(), opts);
}
